import React, { useEffect, useId, useRef, useState } from "react";
import { LuArrowRight, LuTriangleAlert } from "react-icons/lu";
import { LeadRequestApiError, submitLeadRequest } from "api/lead_request/leadRequestApi";
import { EstimateSummaryRow } from "api/lead_request/leadRequestApi.types";
import { COMPANY_SIZES, PRIVACY_URL, TERMS_URL, isWorkEmail, newRequestId } from "../contact";
import { FIELD_CONTROL, FIELD_LABEL, PrimaryButton, SelectField } from "../ui";
import {
  CONTEXT_FIELDS,
  ContextField,
  EnquiryType,
  Option,
  ROLES,
  VARIANTS,
} from "./content";

const TEXT_FIELDS = [
  { name: "name", label: "Full name", type: "text", autoComplete: "name", placeholder: "Jane Tan" },
  { name: "email", label: "Work email", type: "email", autoComplete: "email", placeholder: "jane@yourcompany.com" },
  { name: "organization", label: "Company name", type: "text", autoComplete: "organization", placeholder: "Acme Berhad" },
] as const;

type TextFieldName = (typeof TEXT_FIELDS)[number]["name"];

const asOptions = (values: readonly string[]): Option[] =>
  values.map((value) => ({ value, label: value }));

const COMPANY_SIZE_OPTIONS = asOptions(COMPANY_SIZES);
const ROLE_OPTIONS = asOptions(ROLES);

const EMPTY_VALUES = {
  name: "",
  email: "",
  organization: "",
  company_size: "",
  role: "",
  objective: "",
  participants: "",
  support_level: "",
  starting_option: "",
  mentor_pool: "",
  capability_focus: "",
  demo_focus: "",
  timeline: "",
  notes: "",
};

type Values = typeof EMPTY_VALUES;
type FieldErrors = Partial<Record<keyof Values | "consent", string>>;

// Anything the visitor has already told us elsewhere — the estimator's numbers, or the details they
// gave the business case builder minutes ago. Consent is never carried: it is a fresh permission.
export type Prefill = Partial<Values>;

// Mirrors LeadRequest's validations so the common mistake never costs a round trip. The server
// still revalidates everything — this only decides what the visitor is told first.
function validate(values: Values, consent: boolean, fields: readonly ContextField[]): FieldErrors {
  const errors: FieldErrors = {};

  // Insertion order is the order the summary above the form lists them, so it follows the fields.
  if (!values.name.trim()) errors.name = "Tell us who to reply to.";

  if (!values.email.trim()) errors.email = "Add your work email.";
  else if (!isWorkEmail(values.email)) {
    errors.email = "Use your work email — that's where we'll send our reply.";
  }

  if (!values.organization.trim()) errors.organization = "Add your company name.";

  if (!values.company_size) errors.company_size = "Choose your company size.";
  if (!values.role) errors.role = "Choose the role that best describes you.";

  fields.forEach((field) => {
    if (field === "participants") {
      const participants = Number(values.participants);

      if (!values.participants.trim() || !Number.isFinite(participants) || participants < 1) {
        errors.participants = "Give us a rough number — an estimate is fine.";
      }
      return;
    }

    if (!values[field]) errors[field] = `Choose an option for ${CONTEXT_FIELDS[field].label.toLowerCase()}.`;
  });

  if (!consent) errors.consent = "Tick the box so we can reply to your request.";

  return errors;
}

type Props = {
  enquiryType: EnquiryType;
  prefill?: Prefill;
  estimateSummary?: EstimateSummaryRow[];
  onDraft?: (values: Prefill) => void;
  onSubmitted: (email: string) => void;
};

export default function LeadForm({
  enquiryType,
  prefill,
  estimateSummary,
  onDraft,
  onSubmitted,
}: Props) {
  const variant = VARIANTS[enquiryType];
  const fieldId = useId();

  // A prefill key with no value must not overwrite the empty string a controlled input needs.
  const [values, setValues] = useState<Values>(() => ({
    ...EMPTY_VALUES,
    ...Object.fromEntries(Object.entries(prefill ?? {}).filter(([, value]) => Boolean(value))),
  }));
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [summary, setSummary] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const summaryRef = useRef<HTMLDivElement>(null);
  const requestId = useRef(newRequestId());
  const inFlight = useRef(false);

  // Closing the dialog unmounts this form, so what the visitor typed is handed back before it goes.
  useEffect(() => {
    onDraft?.(values);
  }, [values, onDraft]);

  const setValue = (name: keyof Values, value: string) =>
    setValues((current) => ({ ...current, [name]: value }));

  const showErrors = (messages: string[], fields: FieldErrors = {}) => {
    setFieldErrors(fields);
    setSummary(messages);
    // The summary is the only place server-side messages appear, so it takes focus rather than the
    // first field — otherwise a rejected work address reads as though nothing happened.
    requestAnimationFrame(() => summaryRef.current?.focus());
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (inFlight.current) return;

    const errors = validate(values, consent, variant.fields);

    if (Object.keys(errors).length) {
      showErrors(Object.values(errors) as string[], errors);
      return;
    }

    inFlight.current = true;
    setSubmitting(true);
    setFieldErrors({});
    setSummary([]);

    try {
      await submitLeadRequest({
        request_id: requestId.current,
        website: honeypot,
        lead_request: {
          name: values.name.trim(),
          email: values.email.trim(),
          organization: values.organization.trim(),
          company_size: values.company_size,
          role: values.role,
          consent,
          enquiry_type: enquiryType,
          participants: Number(values.participants),
          ...Object.fromEntries(
            variant.fields.filter((field) => field !== "participants").map((field) => [field, values[field]])
          ),
          notes: values.notes.trim() || undefined,
          estimate_summary: estimateSummary,
        },
      });

      // Last: the panel swaps this form for the success screen, unmounting everything above.
      onSubmitted(values.email.trim());
    } catch (error) {
      const messages = error instanceof LeadRequestApiError ? error.errors : undefined;

      inFlight.current = false;
      setSubmitting(false);
      showErrors(
        messages?.length
          ? messages
          : ["We couldn't send that just now. Check your connection and try again."]
      );
    }
  };

  const describe = (name: keyof FieldErrors) =>
    fieldErrors[name] ? `${fieldId}-${name}-error` : undefined;

  return (
    <form noValidate onSubmit={handleSubmit} className="tw-min-w-0">
      <div
        ref={summaryRef}
        tabIndex={-1}
        role="alert"
        className={
          summary.length
            ? "tw-mb-8 tw-flex tw-items-start tw-gap-3 tw-rounded-2xl tw-border tw-border-[#e8b4b4] tw-bg-[#fdf1f1] tw-p-4 tw-text-[#93231f] focus:tw-outline-none"
            : "tw-hidden"
        }
      >
        <LuTriangleAlert aria-hidden className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0" />
        <ul className="tw-m-0 tw-list-none tw-space-y-1 tw-p-0 tw-text-sm tw-font-bold tw-leading-6">
          {summary.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      </div>

      <Fieldset legend="Your details" hint="Fields marked * are required.">
        {TEXT_FIELDS.map((field) => (
          <TextField
            key={field.name}
            id={`${fieldId}-${field.name}`}
            field={field}
            value={values[field.name]}
            error={fieldErrors[field.name]}
            describedBy={describe(field.name)}
            onChange={(value) => setValue(field.name, value)}
          />
        ))}

        <div className="tw-min-w-0">
          <SelectField
            id={`${fieldId}-company_size`}
            label="Company size *"
            placeholder="Select company size"
            value={values.company_size}
            options={COMPANY_SIZE_OPTIONS}
            invalid={Boolean(fieldErrors.company_size)}
            describedBy={describe("company_size")}
            onChange={(value) => setValue("company_size", value)}
          />
          <FieldError id={`${fieldId}-company_size-error`} message={fieldErrors.company_size} />
        </div>

        <div className="tw-min-w-0">
          <SelectField
            id={`${fieldId}-role`}
            label="Your role *"
            placeholder="Select your role"
            value={values.role}
            options={ROLE_OPTIONS}
            invalid={Boolean(fieldErrors.role)}
            describedBy={describe("role")}
            onChange={(value) => setValue("role", value)}
          />
          <FieldError id={`${fieldId}-role-error`} message={fieldErrors.role} />
        </div>
      </Fieldset>

      <Fieldset legend="Programme context" className="tw-mt-10">
        {variant.fields.map((field) =>
          field === "participants" ? (
            <div key={field} className="tw-min-w-0">
              <label htmlFor={`${fieldId}-participants`} className={FIELD_LABEL}>
                Estimated participants *
              </label>
              <input
                id={`${fieldId}-participants`}
                type="number"
                inputMode="numeric"
                min={1}
                max={1_000_000}
                value={values.participants}
                aria-invalid={fieldErrors.participants ? true : undefined}
                aria-describedby={describe("participants")}
                onChange={(event) => setValue("participants", event.target.value)}
                className={FIELD_CONTROL}
              />
              <FieldError id={`${fieldId}-participants-error`} message={fieldErrors.participants} />
            </div>
          ) : (
            <div key={field} className="tw-min-w-0">
              <SelectField
                id={`${fieldId}-${field}`}
                label={`${CONTEXT_FIELDS[field].label} *`}
                placeholder={CONTEXT_FIELDS[field].placeholder}
                value={values[field]}
                options={CONTEXT_FIELDS[field].options}
                invalid={Boolean(fieldErrors[field])}
                describedBy={describe(field)}
                onChange={(value) => setValue(field, value)}
              />
              <FieldError id={`${fieldId}-${field}-error`} message={fieldErrors[field]} />
            </div>
          )
        )}

        <div className="tw-col-span-2 xs:tw-col-span-1">
          <label htmlFor={`${fieldId}-notes`} className={FIELD_LABEL}>
            Anything else we should know?
          </label>
          <textarea
            id={`${fieldId}-notes`}
            rows={3}
            maxLength={2000}
            placeholder="Optional context, requirements or questions"
            value={values.notes}
            onChange={(event) => setValue("notes", event.target.value)}
            className={`${FIELD_CONTROL} tw-h-auto tw-py-3 tw-leading-6`}
          />
        </div>
      </Fieldset>

      {/* Honeypot: hidden from people, tempting to bots. A filled value is rejected server-side. */}
      <div aria-hidden className="tw-absolute tw-left-[-9999px] tw-h-px tw-w-px tw-overflow-hidden">
        <label htmlFor={`${fieldId}-website`}>Website</label>
        <input
          id={`${fieldId}-website`}
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(event) => setHoneypot(event.target.value)}
        />
      </div>

      <div className="tw-mt-6 tw-rounded-2xl tw-border tw-border-[#c9deed] tw-bg-[#f4f9fd] tw-p-5">
        <label htmlFor={`${fieldId}-consent`} className="tw-m-0 tw-flex tw-items-start tw-gap-3">
          <input
            id={`${fieldId}-consent`}
            type="checkbox"
            checked={consent}
            aria-invalid={fieldErrors.consent ? true : undefined}
            aria-describedby={describe("consent")}
            onChange={(event) => setConsent(event.target.checked)}
            className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-accent-[#087bd3]"
          />
          <span className="tw-text-sm tw-font-normal tw-leading-6 tw-text-[#304d68]">
            I agree that FutureLab may contact me about this request. See FutureLab&apos;s{" "}
            <a href={TERMS_URL} target="_blank" rel="noreferrer" className="tw-font-bold tw-text-[#087bd3] tw-underline">
              terms
            </a>{" "}
            and{" "}
            <a href={PRIVACY_URL} target="_blank" rel="noreferrer" className="tw-font-bold tw-text-[#087bd3] tw-underline">
              privacy policy
            </a>
            .
          </span>
        </label>
        <FieldError id={`${fieldId}-consent-error`} message={fieldErrors.consent} />
      </div>

      <PrimaryButton type="submit" disabled={submitting} className="tw-mt-6 tw-w-full">
        <LuArrowRight aria-hidden className="tw-h-4 tw-w-4 tw-shrink-0" />
        {submitting ? variant.submitting : variant.submit}
      </PrimaryButton>

      <p className="tw-mt-4 tw-mb-0 tw-text-xs tw-leading-5 tw-text-[#5b7188]">
        No commitment. We&apos;ll review what you&apos;ve told us before suggesting a next step.
      </p>
    </form>
  );
}

// A real fieldset so each group is announced as one. `min-w-0` is not optional: a fieldset's default
// `min-width: min-content` stops the grid inside it from ever shrinking.
function Fieldset({
  legend,
  hint,
  className = "",
  children,
}: {
  legend: string;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <fieldset className={`tw-m-0 tw-min-w-0 tw-border-0 tw-p-0 ${className}`}>
      <legend className="tw-mb-0 tw-block tw-w-full tw-p-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.12em] tw-text-[#087bd3]">
        {legend}
      </legend>
      {hint ? <p className="tw-mt-2 tw-mb-0 tw-text-sm tw-text-[#5b7188]">{hint}</p> : null}

      {/* The row gap stays wider than the 8px under a label, so a field reads as a pair rather than
          as two loose lines. */}
      <div className="tw-mt-5 tw-grid tw-grid-cols-2 tw-gap-x-5 tw-gap-y-6 xs:tw-grid-cols-1">
        {children}
      </div>
    </fieldset>
  );
}

function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;

  return (
    <p id={id} className="tw-mt-2 tw-mb-0 tw-text-xs tw-font-bold tw-leading-5 tw-text-[#93231f]">
      {message}
    </p>
  );
}

function TextField({
  id,
  field,
  value,
  error,
  describedBy,
  onChange,
}: {
  id: string;
  field: (typeof TEXT_FIELDS)[number];
  value: string;
  error?: string;
  describedBy?: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className={`tw-min-w-0 ${field.name === "organization" ? "tw-col-span-2 xs:tw-col-span-1" : ""}`}>
      <label htmlFor={id} className={FIELD_LABEL}>
        {field.label} *
      </label>
      <input
        id={id}
        type={field.type}
        autoComplete={field.autoComplete}
        placeholder={field.placeholder}
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        onChange={(event) => onChange(event.target.value)}
        className={FIELD_CONTROL}
      />
      <FieldError id={`${id}-error`} message={error} />
    </div>
  );
}
